import type { AgGridReact } from '@mint-lib/ag-grid/react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { FolderAdd, Information } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQuery, useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import { Button, Card } from '@mint-lib/tw-components';
import { SearchBar, type BaseFile } from '@mint-lib/tw-components/patterns';
import { KnowledgeBase as KB } from '@mint-sdk/api-ai-assistant';
import { useAtom } from 'jotai';

import type {
  MoveRejection,
  MoveTarget,
} from '../../helpers/knowledge-base/fileSystemMove.js';
import {
  canManageFolder,
  canShareFolder,
} from '../../helpers/knowledge-base/knowledgeBaseAccess.js';
import { getKnowledgeBaseLayout } from '../../helpers/knowledge-base/knowledgeBaseLayout.js';
import { useCreateFolder } from '../../hooks/knowledge-base/useCreateFolder.js';
import { useCurrentFolder } from '../../hooks/knowledge-base/useCurrentFolder.jsx';
import { useDeleteNodesDialog } from '../../hooks/knowledge-base/useDeleteNodesDialog.js';
import { useDocumentActions } from '../../hooks/knowledge-base/useDocumentActions.js';
import { useFileSystemNodes } from '../../hooks/knowledge-base/useFileSystemNodes.js';
import { useFolderMutations } from '../../hooks/knowledge-base/useFolderMutations.js';
import { useFolderNavigation } from '../../hooks/knowledge-base/useFolderNavigation.js';
import {
  RowActionsContext,
  useKnowledgeBaseAccess,
  useRowActions,
} from '../../hooks/knowledge-base/useKnowledgeBaseAccess.js';
import { useKnowledgeBaseSelection } from '../../hooks/knowledge-base/useKnowledgeBaseSelection.js';
import { useMoveNodes } from '../../hooks/knowledge-base/useMoveNodes.js';
import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import { useSearchValue } from '../../hooks/useSearchValue.js';
import { forgetListings } from '../../queries/knowledgeBase.js';
import { getDocumentsOption } from '../../queries/rag.js';
import { openFolderIdAtom } from '../../store/fileSystem.js';
import {
  canNestAt,
  MAX_FOLDER_DEPTH,
  ROOT_FOLDER_ID,
  toApiFolderId,
  toListedFolderId,
  USERS_PRIVATE_FOLDERS_ID,
  type FileSystemNode,
  type FolderCrumb,
  type FolderNode,
} from '../../types/fileSystem.js';
import { RagTableType } from '../../types/rag.js';
import { DeleteNodesDialog } from './DeleteNodesDialog.jsx';
import { EmptyFolderDropzone } from './EmptyFolderDropzone.jsx';
import { EmptyState, FolderErrorState } from './EmptyState.jsx';
import { FileSystemBreadcrumbs } from './FileSystem/Breadcrumbs/Breadcrumbs.jsx';
import { FileSystemUsage } from './FileSystem/FileSystemUsage/FileSystemUsage.jsx';
import { FolderParentActions } from './FileSystem/FolderSection/FolderParentActions.jsx';
import { MoveNodesDialog } from './FileSystem/MoveNodesDialog/MoveNodesDialog.jsx';
import { FileSystemTable } from './FileSystem/TableView/FileSystemTable.jsx';
import { PrivateFoldersSection } from './PrivateFolders/PrivateFoldersSection.jsx';
import { UsersPrivateFolders } from './PrivateFolders/UsersPrivateFolders.jsx';
import { RagTable } from './RagTable/RagTable.jsx';
import { RulesDialog } from './RulesDialog.jsx';
import { CreateSharedFolderDialog } from './SharedFolders/CreateSharedFolderDialog.jsx';
import { SharedFoldersHeader } from './SharedFolders/SharedFoldersHeader.jsx';
import { SharedWithPopover } from './SharedFolders/SharedWithPopover.jsx';
import { ShareFolderDialog } from './SharedFolders/ShareFolderDialog.jsx';
import { ToolbarActions } from './ToolbarActions.jsx';
import { UploadFileDialog } from './UploadFileDialog.jsx';

type UploadTarget = {
  depth: number;
  isInOwnPrivateBucket: boolean;
  breadcrumbItems: FolderCrumb[];
};

const getUploadTargetName = (
  { depth, isInOwnPrivateBucket, breadcrumbItems }: UploadTarget,
  t: TFunction,
): string | undefined => {
  if (!depth) return undefined;
  if (depth === 1 && isInOwnPrivateBucket) return t('My private folder');

  return breadcrumbItems.at(-1)?.name;
};

const KnowledgeBase = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const queryClient = useQueryClient();
  const ff = useFlags('@mint/arm', ['aiFileSystem', 'aiKnowledgeBaseStorage']);

  const gridRef = useRef<AgGridReact | null>(null);

  const [movingNodes, setMovingNodes] = useState<FileSystemNode[] | null>(null);
  const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false);
  const [pickedFiles, setPickedFiles] = useState<BaseFile[]>([]);
  const [isOpenRulesDialog, setIsOpenRulesDialog] = useState(false);
  const [isOpenSharedFolderDialog, setIsOpenSharedFolderDialog] =
    useState(false);
  const [sharingFolder, setSharingFolder] = useState<FolderNode | null>(null);

  const [currentFolderId, setCurrentFolderId] = useAtom(openFolderIdAtom);

  const isUsersPrivateFolders = currentFolderId === USERS_PRIVATE_FOLDERS_ID;

  const listedFolderId = toListedFolderId(currentFolderId);

  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);

  const { onDocumentsBulkDownload } = useDocumentActions();
  const { canUploadKBFile } = useAiHubPermissions();
  const access = useKnowledgeBaseAccess();

  const {
    value: searchValue,
    debouncedValue: debouncedSearchValue,
    onChange: handleChangeSearch,
    onClear: handleClearSearch,
  } = useSearchValue();

  const isStorageAvailable =
    ff.aiKnowledgeBaseStorage.enabled && canUploadKBFile;

  const { data: documentData = [] } = useQuery(getDocumentsOption());

  const {
    data: storageData,
    isLoading: isStorageLoading,
    isError: isStorageError,
  } = useQuery({
    ...KB.getKnowledgeBaseStorage({}),
    enabled: isStorageAvailable,
  });

  const {
    folders,
    files,
    privateBuckets,
    breadcrumbItems: listedBreadcrumbItems,
    currentPrivateBucket,
    ownPrivateBucket,
    isInOwnPrivateBucket,
    depth,
    isLoading: isLoadingNodes,
    isError: isNodesError,
    refetch: refetchNodes,
  } = useFileSystemNodes({
    folderId: listedFolderId,
    search: debouncedSearchValue,
  });

  const existingFileNames = useMemo(
    () =>
      ff.aiFileSystem.enabled
        ? files.map(({ fileName }) => fileName)
        : documentData.map(({ filename }) => filename),
    [ff.aiFileSystem.enabled, files, documentData],
  );

  const myPrivateFolderId = ownPrivateBucket?.id ?? null;

  const canCreateOwnPrivateFolder =
    access.canWrite && !isLoadingNodes && !isNodesError;

  const { createPrivateBucket, isPending: isCreatingPrivateBucket } =
    useFolderMutations();

  const breadcrumbItems = useMemo(() => {
    if (
      !access.isKbAdmin ||
      !currentPrivateBucket ||
      currentPrivateBucket.id === myPrivateFolderId
    ) {
      return listedBreadcrumbItems;
    }

    const [root, ...rest] = listedBreadcrumbItems;

    return [
      root,
      { folderId: USERS_PRIVATE_FOLDERS_ID, name: t('Users private folders') },
      ...rest,
    ];
  }, [
    access.isKbAdmin,
    currentPrivateBucket,
    myPrivateFolderId,
    listedBreadcrumbItems,
    t,
  ]);

  const uploadTargetName = getUploadTargetName(
    { depth, isInOwnPrivateBucket, breadcrumbItems },
    t,
  );

  const othersPrivateBuckets = useMemo(
    () => privateBuckets.filter(({ id }) => id !== myPrivateFolderId),
    [privateBuckets, myPrivateFolderId],
  );

  const {
    data: currentFolder,
    folderFormat: currentFolderFormat,
    isLoading: isLoadingCurrentFolder,
  } = useCurrentFolder(listedFolderId);

  const selection = useKnowledgeBaseSelection({ folders, gridRef });
  const deleteDialog = useDeleteNodesDialog();

  const {
    isDrafting,
    startDraft,
    cancelDraft,
    commitDraft,
    renameFolder,
    validateName,
  } = useCreateFolder({
    parentId: currentFolderId,
    siblings: folders,
  });

  const canWriteHere = ff.aiFileSystem.enabled
    ? access.canWrite
    : canUploadKBFile;

  const canCreateFolder = canWriteHere && canNestAt(depth);

  const isBucketRoot =
    ff.aiFileSystem.enabled && currentFolderId === ROOT_FOLDER_ID;

  const isFolderManageable = useCallback(
    (folder: FolderNode) => canManageFolder(folder, access),
    [access],
  );

  const isFolderShareable = useCallback(
    (folder: FolderNode) => canShareFolder(folder, access),
    [access],
  );

  const rowActions = useRowActions();

  const canDeleteRow = (row: RagTableType) =>
    rowActions.getActions(row).includes('delete');

  const canDownloadRow = (row: RagTableType) =>
    rowActions.getActions(row).includes('download');

  const canDeleteSelection =
    selection.folders.every(isFolderManageable) &&
    selection.rows.every(canDeleteRow);

  const nodes = useMemo<FileSystemNode[]>(
    () => [...folders, ...files],
    [folders, files],
  );

  const onCommitRename = useCallback(
    (id: string, name: string) => {
      const renamed = renameFolder(id, name);

      if (renamed) setRenamingFolderId(null);

      return renamed;
    },
    [renameFolder],
  );

  const onCancelRename = useCallback(() => setRenamingFolderId(null), []);

  const { bodyView, showToolbar } = getKnowledgeBaseLayout({
    isFileSystemEnabled: ff.aiFileSystem.enabled,
    hasDocuments: documentData.length > 0,
    isLoading: isLoadingNodes,
    isError: isNodesError,
    hasNodes: nodes.length > 0,
    isDrafting,
    hasActiveSearch: debouncedSearchValue.trim().length > 0,
    canWrite: canWriteHere,
    isBucketRoot,
    hasSelection: selection.count > 0,
  });

  const searchPlaceholder = ff.aiFileSystem.enabled
    ? t('Search a file or a folder')
    : t('Search a file');

  const onOpenUploadDialog = () => {
    setIsOpenUploadDialog(true);
  };

  const onCloseUploadDialog = () => {
    setIsOpenUploadDialog(false);
    setPickedFiles([]);
  };

  const handleFilesPicked = (files: BaseFile[]) => {
    setPickedFiles(files);
    setIsOpenUploadDialog(true);
  };

  const handleToggleRulesDialog = () => {
    setIsOpenRulesDialog((p) => !p);
  };

  const { clear: clearSelection } = selection;

  const resetFolderScopedState = useCallback(() => {
    clearSelection();
    setRenamingFolderId(null);
    cancelDraft();
  }, [clearSelection, cancelDraft]);

  const showFolder = useCallback(
    (folderId: string) => {
      resetFolderScopedState();
      setCurrentFolderId(folderId);
    },
    [resetFolderScopedState, setCurrentFolderId],
  );

  // The current folder stays on screen until the next one has loaded.
  const { openFolder, pendingFolderId } = useFolderNavigation({
    onOpen: showFolder,
  });

  const handleOpenFolder = useCallback(
    (folder: FolderNode) => openFolder(folder.id),
    [openFolder],
  );

  const { open: openDeleteDialog } = deleteDialog;

  const handleOpenDeleteSelection = () =>
    openDeleteDialog(selection.rows, selection.folders);

  const handleDownloadSelection = () =>
    onDocumentsBulkDownload(selection.rows, clearSelection);

  const handleOpenDeleteFolder = useCallback(
    (folder: FolderNode) => openDeleteDialog([], [folder]),
    [openDeleteDialog],
  );

  const leaveDeletedFolders = useEvent((folderIds: string[]) => {
    const deleted = new Set(folderIds);
    const index = breadcrumbItems.findIndex(({ folderId }) =>
      deleted.has(folderId),
    );

    if (index <= 0) return;

    forgetListings(
      queryClient,
      breadcrumbItems.slice(index).map(({ folderId }) => folderId),
    );
    openFolder(breadcrumbItems[index - 1].folderId, { fresh: true });
  });

  const { checkMove, canMoveFrom, moveNodes } = useMoveNodes({
    currentFolderId,
    access,
  });

  const canDownloadSelection = selection.rows.every(canDownloadRow);

  const canMoveSelection =
    selection.nodes.length > 0 && selection.nodes.every(canMoveFrom);

  const describeRejection = useCallback(
    (reason: MoveRejection) => {
      switch (reason) {
        case 'root-not-allowed':
          return t('Items can only be moved into a folder');
        case 'descendant':
        case 'self':
          return t("A folder can't be moved inside itself");
        case 'max-depth':
          return t('Folders can only be nested {{levels}} levels deep', {
            levels: MAX_FOLDER_DEPTH,
          });
        case 'name-taken':
          return t('An item with this name already exists in the folder');
        case 'file-not-ready':
          return t('Files that are not processed can not be moved');
        case 'no-permission':
          return t('You do not have permission to move items here');
        default:
          return null;
      }
    },
    [t],
  );

  const handleMoveNodes = useCallback(
    async (nodes: FileSystemNode[], target: MoveTarget) => {
      const { moved, reason } = await moveNodes(nodes, target);

      if (moved) {
        clearSelection();
        return true;
      }

      const message = reason && describeRejection(reason);

      if (message) {
        enqueueSnackbar({
          variant: 'warning',
          subTitle: message,
          removable: true,
        });
      }

      return false;
    },
    [moveNodes, clearSelection, describeRejection],
  );

  const onOpenMoveDialog = setMovingNodes;

  return (
    <>
      <div className="flex h-full flex-col gap-5">
        {isBucketRoot && (
          <>
            <PrivateFoldersSection
              canSeeOthersPrivateFolders={access.isKbAdmin}
              myPrivateFolderId={myPrivateFolderId}
              canCreateOwnPrivateFolder={canCreateOwnPrivateFolder}
              isCreatingOwnPrivateFolder={isCreatingPrivateBucket}
              onOpenOwnPrivateFolder={openFolder}
              onCreateOwnPrivateFolder={async () => {
                const folderId = await createPrivateBucket();

                if (folderId) openFolder(folderId);
              }}
              onOpenUsersPrivateFolders={() =>
                openFolder(USERS_PRIVATE_FOLDERS_ID)
              }
            />

            <SharedFoldersHeader
              canCreateSharedFolder={access.isKbAdmin && !selection.count}
              searchValue={searchValue}
              onCreateSharedFolder={() => setIsOpenSharedFolderDialog(true)}
              onChangeSearch={handleChangeSearch}
              onClearSearch={handleClearSearch}
            />
          </>
        )}

        {isUsersPrivateFolders ? (
          <UsersPrivateFolders
            buckets={othersPrivateBuckets}
            isLoading={isLoadingNodes}
            searchValue={searchValue}
            search={debouncedSearchValue}
            onChangeSearch={handleChangeSearch}
            onClearSearch={handleClearSearch}
            onOpenFolder={openFolder}
          />
        ) : (
          <RowActionsContext.Provider value={rowActions}>
            {ff.aiFileSystem.enabled && !isBucketRoot && (
              <div className="flex flex-row items-center justify-between">
                <FileSystemBreadcrumbs
                  items={breadcrumbItems}
                  onFolderChange={openFolder}
                />

                {access.isKbAdmin &&
                  currentFolder?.isBucket &&
                  !currentFolder?.isPrivate && (
                    <SharedWithPopover
                      folder={currentFolder}
                      isLoading={isLoadingCurrentFolder}
                    />
                  )}
              </div>
            )}

            {showToolbar && (
              <Card className="flex w-full flex-row" contentClassName="w-full">
                <div className="flex w-full flex-row justify-between">
                  <div className="flex flex-row items-center gap-3">
                    <ToolbarActions
                      selectedCount={selection.count}
                      isFileSystemEnabled={ff.aiFileSystem.enabled}
                      canWrite={canWriteHere}
                      canDownloadKBFile={canDownloadSelection}
                      canDeleteSelection={canDeleteSelection}
                      canMoveSelection={canMoveSelection}
                      hasSelectedFolders={selection.folders.length > 0}
                      onOpenUploadDialog={onOpenUploadDialog}
                      onDownloadSelection={handleDownloadSelection}
                      onDeleteSelection={handleOpenDeleteSelection}
                      onOpenMoveDialog={() => onOpenMoveDialog(selection.nodes)}
                    />
                    {ff.aiFileSystem.enabled &&
                      canCreateFolder &&
                      !selection.count && (
                        <Button
                          variant={'outlined'}
                          onClick={startDraft}
                          data-dd-action-name="Knowledge base: create folder"
                          disabled={isDrafting}
                        >
                          <FolderAdd />
                          {t('Create folder')}
                        </Button>
                      )}
                    {ff.aiFileSystem.enabled &&
                      !selection.count &&
                      currentFolderFormat &&
                      isFolderManageable(currentFolderFormat) && (
                        <FolderParentActions
                          folderId={currentFolderId}
                          onCommitRename={onCommitRename}
                          onCancelRename={onCancelRename}
                          initialValue={currentFolder?.name}
                          validateName={validateName}
                          onDelete={() =>
                            handleOpenDeleteFolder(currentFolderFormat)
                          }
                          canShare={isFolderShareable(currentFolderFormat)}
                          onShare={() => setSharingFolder(currentFolderFormat)}
                        />
                      )}
                  </div>

                  {!isBucketRoot && (
                    <div className="flex flex-row gap-2">
                      {canUploadKBFile &&
                        !ff.aiKnowledgeBaseStorage.enabled && (
                          <Button
                            variant="text"
                            className="flex flex-row gap-2"
                            data-testid="uploading-rules-btn"
                            data-dd-action-name="Knowledge base: open uploading rules"
                            onClick={handleToggleRulesDialog}
                          >
                            <Information />
                            {t('Uploading rules')}
                          </Button>
                        )}

                      <SearchBar
                        placeholder={searchPlaceholder}
                        className="h-8 w-75 max-w-75"
                        onChange={handleChangeSearch}
                        onClear={handleClearSearch}
                        value={searchValue}
                      />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {bodyView === 'error' && (
              <FolderErrorState onRetry={() => void refetchNodes()} />
            )}

            {bodyView === 'table' && ff.aiFileSystem.enabled && (
              <FileSystemTable
                gridRef={gridRef}
                isBucketRoot={isBucketRoot}
                tableData={nodes}
                isLoading={isLoadingNodes}
                openingFolderId={pendingFolderId}
                onSelectNodes={selection.selectNodes}
                onUnselectTableRows={clearSelection}
                onOpenFolder={handleOpenFolder}
                onOpenDeleteDialog={openDeleteDialog}
                onOpenDeleteFolder={handleOpenDeleteFolder}
                canManageFolder={isFolderManageable}
                canMoveFolder={canMoveFrom}
                canShareFolder={isFolderShareable}
                onShareFolder={setSharingFolder}
                validateName={validateName}
                draft={{
                  isDrafting,
                  onCommit: commitDraft,
                  onCancel: cancelDraft,
                }}
                rename={{
                  folderId: renamingFolderId,
                  onStart: setRenamingFolderId,
                  onCommit: onCommitRename,
                  onCancel: onCancelRename,
                }}
                onOpenMoveDialog={onOpenMoveDialog}
              />
            )}

            {/* aiFileSystem cleanup: delete this table and its import. */}
            {bodyView === 'table' && !ff.aiFileSystem.enabled && (
              <RagTable
                gridRef={gridRef}
                tableData={files}
                onSelectTableRows={selection.selectRows}
                onUnselectTableRows={clearSelection}
                onOpenDeleteDialog={openDeleteDialog}
              />
            )}

            {bodyView === 'dropzone' && (
              <EmptyFolderDropzone
                existingFileNames={existingFileNames}
                onFilesPicked={handleFilesPicked}
              />
            )}

            {bodyView === 'empty' && (
              <EmptyState
                isFileSystemEnabled={ff.aiFileSystem.enabled}
                canUploadKBFile={canUploadKBFile}
                onOpenUploadDialog={onOpenUploadDialog}
              />
            )}
          </RowActionsContext.Provider>
        )}

        {isStorageAvailable && (
          <FileSystemUsage
            onOpenRules={handleToggleRulesDialog}
            storageData={storageData}
            isLoading={isStorageLoading}
            isError={isStorageError}
          />
        )}
      </div>

      <UploadFileDialog
        isOpen={isOpenUploadDialog}
        onClose={onCloseUploadDialog}
        existingFileNames={existingFileNames}
        targetFolderId={toApiFolderId(currentFolderId)}
        targetFolderName={uploadTargetName}
        initialFiles={pickedFiles}
      />

      <RulesDialog
        isOpen={isOpenRulesDialog}
        onClose={handleToggleRulesDialog}
      />

      {isOpenSharedFolderDialog && (
        <CreateSharedFolderDialog
          validateName={validateName}
          onClose={() => setIsOpenSharedFolderDialog(false)}
        />
      )}

      {sharingFolder && (
        <ShareFolderDialog
          folderId={sharingFolder.id}
          folderName={sharingFolder.name}
          onClose={() => setSharingFolder(null)}
        />
      )}

      <DeleteNodesDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        documents={deleteDialog.documents}
        folders={deleteDialog.folders}
        onUnselectTableRows={clearSelection}
        onFoldersDeleted={leaveDeletedFolders}
      />

      {ff.aiFileSystem.enabled && movingNodes && (
        <MoveNodesDialog
          nodes={movingNodes}
          onMoveNodes={handleMoveNodes}
          canMoveNodes={checkMove}
          open
          onClose={() => setMovingNodes(null)}
        />
      )}
    </>
  );
};

export default KnowledgeBase;
